import { Head, router, Link, useForm } from '@inertiajs/react'
import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import PageHeader from '@/components/common/PageHeader'
import PatientHamburgerMenu from '@/components/common/PatientHamburgerMenu'
import WeeklyCalendar from '@/components/reservations/WeeklyCalendar'
import PrescriptionNotice from '@/components/reservations/PrescriptionNotice'
import type { MedicalDepartment, Clinic, Doctor, DoctorSchedule, TimeRow } from '@/types/schedule'

type Props = {
  medical_departments: MedicalDepartment[]
  selected_medical_department: MedicalDepartment | null
  selectable_clinics: Clinic[]
  selected_clinic: Clinic | null
  selectable_doctors: Doctor[]
  selected_doctor_id: number | null
  medical_department_notice: string | null
  weekly_schedules: TimeRow[]
  week_start_date: string
  earliest_schedule: DoctorSchedule | null
  selected_doctor_schedule_id: string | null
  is_logged_in: boolean
  // 診療TOPから受診者を決めて入った場合のみ入る。仮予約に引き継ぎ、受診者選択を挟まず問診へ進む(#1060)
  patient_family_profile_id: string | null
}


// 日付文字列をローカルタイムゾーンでパース（UTC解釈によるズレを防止）
function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

// ブラウザがスクロールアンカリング（レイアウトが変わっても見ている位置を保つ機能）を持つか。Safari は未対応
const hasScrollAnchoring = typeof CSS !== 'undefined' && CSS.supports('overflow-anchor', 'auto')

// 日付をYYYY-MM-DD形式の文字列に変換（ローカルタイムゾーン）
function formatDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default function SelectSchedule({
  medical_departments,
  selected_medical_department,
  selectable_clinics,
  selected_clinic,
  selectable_doctors,
  selected_doctor_id,
  medical_department_notice,
  weekly_schedules,
  week_start_date,
  earliest_schedule,
  selected_doctor_schedule_id,
  is_logged_in,
  patient_family_profile_id,
}: Props) {
  // useFormで予約フォームを管理
  const form = useForm({
    doctor_schedule_id: selected_doctor_schedule_id ?? '',
    medical_department_id: selected_medical_department?.id ?? '',
    clinic_id: selected_clinic?.id ?? '',
    doctor_id: selected_doctor_id ?? '',
    patient_family_profile_id: patient_family_profile_id ?? '',
  })

  // 週間スケジュールとearliest_scheduleはサーバーから受け取った値をstateで管理
  // （partial reloadで更新される）
  const [weeklySchedules, setWeeklySchedules] = useState<TimeRow[]>(weekly_schedules)
  const [currentWeekStart, setCurrentWeekStart] = useState(week_start_date)
  const [currentEarliestSchedule, setCurrentEarliestSchedule] = useState<DoctorSchedule | null>(earliest_schedule)
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false)
  const [selectedScheduleInfo, setSelectedScheduleInfo] = useState<{
    date: string
    time: string
    doctorName: string
    expectedPrescriptionIssuedDate: string | null
    nightSlot: boolean
    pharmacyReceivableFrom: string | null
  } | null>(null)

  // 選択中バナーの高さ（カレンダーの見出しをバナーの下に追従させるため。文言の折り返しで高さが変わるので実測する）
  const [bannerHeight, setBannerHeight] = useState(0)
  const bannerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    // 描画前に見出しの位置を決められるよう、まず同期で測ってから変化を監視する
    setBannerHeight(node.offsetHeight)
    const observer = new ResizeObserver(() => setBannerHeight(node.offsetHeight))
    observer.observe(node)
    return () => {
      observer.disconnect()
      setBannerHeight(0)
    }
  }, [])

  const selectedClinicId = form.data.clinic_id ? Number(form.data.clinic_id) : null
  const isBannerShown = selectedScheduleInfo !== null && selectedClinicId !== null
  // バナーは fixed なので、出ている間はその高さ分の余白をページ上部に入れて PageHeader が隠れないようにする
  const spacerHeight = isBannerShown ? bannerHeight : 0

  // 余白の増減（枠の選択・週送りによる選択解除）で表が上下にずれないよう、
  // スクロールアンカリングの無いブラウザ（Safari）では同じ補正を自前で行う。Chrome/Firefox はブラウザが自動で行う
  const prevSpacerHeightRef = useRef(0)
  const lastScrollYRef = useRef(0)
  useEffect(() => {
    if (hasScrollAnchoring) return
    const onScroll = () => { lastScrollYRef.current = window.scrollY }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  useLayoutEffect(() => {
    const delta = spacerHeight - prevSpacerHeightRef.current
    prevSpacerHeightRef.current = spacerHeight
    // ページ最上部ではアンカリングと同じく補正しない（余白が増えた分だけ下へ押し出されて見えるのが自然）
    if (hasScrollAnchoring || delta === 0 || lastScrollYRef.current === 0) return
    // 余白が消えてページが短くなると、スクロール位置はレイアウト時に下限へ丸められることがあるため、相対ではなく絶対位置で指定する
    window.scrollTo(0, lastScrollYRef.current + delta)
    lastScrollYRef.current = window.scrollY
  }, [spacerHeight])

  // ブラウザバックでキャッシュ表示された場合にリロードしてサーバーサイドチェックを実行
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload()
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  // propsが更新されたらstateも更新
  useEffect(() => {
    setWeeklySchedules(weekly_schedules)
    setCurrentEarliestSchedule(earliest_schedule)
    form.setData('doctor_id', selected_doctor_id ?? '')
  }, [weekly_schedules, earliest_schedule, selected_doctor_id])

  // スケジュール取得（partial reload）
  const fetchSchedules = useCallback(
    (
      medicalDepartmentId: number | string,
      clinicId: number | string,
      weekStart: string,
      doctorId: number | string,
    ) => {
      setIsLoadingSchedules(true)

      router.reload({
        data: {
          medical_department_id: medicalDepartmentId,
          clinic_id: clinicId,
          week_start: weekStart,
          doctor_id: doctorId,
        },
        only: ['weekly_schedules', 'earliest_schedule', 'selectable_doctors', 'selected_doctor_id'],
        onFinish: () => setIsLoadingSchedules(false),
      })
    },
    []
  )

  // 診療科目変更時
  const handleMedicalDepartmentChange = useCallback((departmentId: number) => {
    form.setData({
      doctor_schedule_id: '',
      medical_department_id: departmentId,
      clinic_id: '',
      doctor_id: '',
      // 診療科目を変えても受診者の指定は保つ(#1060)
      patient_family_profile_id: form.data.patient_family_profile_id,
    })
    setSelectedScheduleInfo(null)

    // ページリロードでクリニック一覧を更新
    router.get(
      '/online_clinic/reservations/select_schedule',
      {
        medical_department_id: departmentId,
        ...(patient_family_profile_id ? { patient_family_profile_id } : {}),
      },
      { preserveState: false }
    )
  }, [form, patient_family_profile_id])

  // クリニック変更時（医師フィルターはクリニックに紐づくのでリセット）
  const handleClinicChange = useCallback(
    (clinicId: number) => {
      form.setData({ ...form.data, clinic_id: clinicId, doctor_schedule_id: '', doctor_id: '' })
      setSelectedScheduleInfo(null)

      if (form.data.medical_department_id) {
        fetchSchedules(form.data.medical_department_id, clinicId, currentWeekStart, '')
      }
    },
    [form, currentWeekStart, fetchSchedules]
  )

  // 医師フィルター変更時
  const handleDoctorChange = useCallback(
    (doctorId: number | '') => {
      form.setData({ ...form.data, doctor_id: doctorId, doctor_schedule_id: '' })
      setSelectedScheduleInfo(null)

      if (form.data.medical_department_id && form.data.clinic_id) {
        fetchSchedules(form.data.medical_department_id, form.data.clinic_id, currentWeekStart, doctorId)
      }
    },
    [form, currentWeekStart, fetchSchedules]
  )

  // 週変更時
  const handleWeekChange = useCallback(
    (direction: 'prev' | 'next') => {
      if (!form.data.medical_department_id || !form.data.clinic_id) return

      const currentDate = parseLocalDate(currentWeekStart)
      currentDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7))
      const newWeekStart = formatDateString(currentDate)

      setCurrentWeekStart(newWeekStart)
      form.setData('doctor_schedule_id', '')
      setSelectedScheduleInfo(null)

      fetchSchedules(form.data.medical_department_id, form.data.clinic_id, newWeekStart, form.data.doctor_id)
    },
    [form, currentWeekStart, fetchSchedules]
  )

  // スケジュール選択時
  const handleSelectSchedule = useCallback((scheduleId: string) => {
    form.setData('doctor_schedule_id', scheduleId)
    form.clearErrors()

    // weeklySchedulesから該当slotを検索して選択情報をセット
    for (const row of weeklySchedules) {
      for (const slot of row.slots) {
        if (slot.doctor_schedule_id === scheduleId && slot.start && slot.end) {
          const startDate = new Date(slot.start)
          const endDate = new Date(slot.end)
          const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][startDate.getDay()]
          const date = `${String(startDate.getMonth() + 1).padStart(2, '0')}/${String(startDate.getDate()).padStart(2, '0')}（${dayOfWeek}）`
          const time = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}〜${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
          setSelectedScheduleInfo({
            date,
            time,
            doctorName: slot.doctor_name ?? '',
            expectedPrescriptionIssuedDate: slot.expected_prescription_issued_date ?? null,
            nightSlot: slot.night_slot ?? false,
            pharmacyReceivableFrom: slot.pharmacy_receivable_from ?? null,
          })
          return
        }
      }
    }
  }, [form, weeklySchedules])

  // 一番近い日時で予約（即時サブミット）
  const [isSubmittingEarliest, setIsSubmittingEarliest] = useState(false)
  const handleSelectEarliest = useCallback(() => {
    if (!currentEarliestSchedule || isSubmittingEarliest) return

    setIsSubmittingEarliest(true)

    const data = {
      doctor_schedule_id: currentEarliestSchedule.id,
      medical_department_id: form.data.medical_department_id,
      clinic_id: form.data.clinic_id,
      patient_family_profile_id: form.data.patient_family_profile_id,
    }

    if (is_logged_in) {
      router.post('/online_clinic/provisional_reservations', data)
    } else {
      router.post('/online_clinic/reservations/save_selection', data)
    }
  }, [form.data, currentEarliestSchedule, is_logged_in, isSubmittingEarliest])

  // 予約するボタンクリック時
  const handleSubmit = useCallback(() => {
    form.post('/online_clinic/provisional_reservations')
  }, [form])

  // 未ログイン時のログインへボタン
  const handleLoginRedirect = useCallback(() => {
    router.post('/online_clinic/reservations/save_selection', {
      medical_department_id: form.data.medical_department_id,
      clinic_id: form.data.clinic_id,
      doctor_schedule_id: form.data.doctor_schedule_id,
    })
  }, [form.data])

  const canSubmit = form.data.medical_department_id && form.data.clinic_id && form.data.doctor_schedule_id

  // 現在選択中のクリニック一覧
  const currentClinics = selectable_clinics

  return (
    <>
      <Head title="予約枠を選択 - オンライン診療" />

      {/* 選択済みスケジュール確認バナー（フロート） */}
      {isBannerShown && (
        <div ref={bannerRef} className="fixed top-0 left-0 right-0 z-20 bg-cyan-50 border-b border-cyan-300 shadow-sm">
          <div className="mx-auto max-w-3xl px-4 py-2 flex items-start gap-2">
            <span className="text-cyan-600 mt-0.5">&#10003;</span>
            <div>
              <p className="text-sm font-medium text-gray-700">選択中の予約枠</p>
              <p className="text-base font-bold text-gray-900">
                {selectedScheduleInfo.date}　{selectedScheduleInfo.time}
                {selectedScheduleInfo.doctorName && ` / ${selectedScheduleInfo.doctorName} 先生`}
              </p>
              <PrescriptionNotice
                nightSlot={selectedScheduleInfo.nightSlot}
                expectedPrescriptionIssuedDate={selectedScheduleInfo.expectedPrescriptionIssuedDate}
                pharmacyReceivableFrom={selectedScheduleInfo.pharmacyReceivableFrom}
                className="mt-0.5"
              />
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-white">
        {/* フロートバナー分のスペース確保 */}
        {isBannerShown && <div style={{ height: spacerHeight }} />}

        <PageHeader
          trailing={
            is_logged_in ? (
              <PatientHamburgerMenu />
            ) : (
              <Link
                href="/users/sign_in?reservation=1"
                className="whitespace-nowrap px-4 py-2 bg-cyan-500 text-white text-sm rounded-full hover:bg-cyan-600"
              >
                ログイン
              </Link>
            )
          }
        />

        {/* Main Content */}
        <main className="mx-auto max-w-3xl px-4 py-6">
          {/* 診療科目選択 */}
          <div className="mb-6">
            <label className="block text-sm text-gray-600 mb-2">
              <span className="font-medium">診療科目</span>
              <span className="text-gray-400 ml-2">不明な場合は【総合内科】を選択してください。</span>
            </label>
            <select
              value={form.data.medical_department_id}
              onChange={(e) => handleMedicalDepartmentChange(Number(e.target.value))}
              className="w-full box-border text-[14px] bg-[#f3f4f5] border-none pl-[28px] pr-[14px] py-[14px] rounded-[50px] appearance-none focus-visible:outline-none"
            >
              <option value="">診療科目を選択してください</option>
              {medical_departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* クリニック選択 */}
          {form.data.medical_department_id && (
            <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">連携診療機関</label>
              {currentClinics.length > 0 ? (
                <div className="space-y-3">
                  {currentClinics.map((clinic) => (
                    <label
                      key={clinic.id}
                      className="flex items-start cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="clinic_id"
                        value={clinic.id}
                        checked={selectedClinicId === clinic.id}
                        onChange={() => handleClinicChange(clinic.id)}
                        className="mt-1 mr-3 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-gray-700">{clinic.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center">
                  <p className="text-sm text-gray-500">現在、この診療科目では予約を受け付けておりません。</p>
                  <p className="text-xs text-gray-400 mt-1">他の診療科目をお選びください。</p>
                </div>
              )}

              {medical_department_notice && (
                <div className="mt-4 text-sm text-gray-600">
                  <p className="text-gray-500">{medical_department_notice}</p>
                </div>
              )}
            </div>
          )}

          {/* 医師フィルター */}
          {selectedClinicId && selectable_doctors.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm text-gray-600 mb-2">
                <span className="font-medium">医師を指名する</span>
              </label>
              <select
                value={form.data.doctor_id}
                onChange={(e) => handleDoctorChange(e.target.value ? Number(e.target.value) : '')}
                className="w-full box-border text-[14px] bg-[#f3f4f5] border-none pl-[28px] pr-[14px] py-[14px] rounded-[50px] appearance-none focus-visible:outline-none"
              >
                <option value="">指名なし</option>
                {selectable_doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.full_name} 先生
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 最短予約枠表示 */}
          {currentEarliestSchedule && selectedClinicId && (
            <div className="mb-6 rounded-lg border border-cyan-200 bg-cyan-50 overflow-hidden">
              <div className="px-4 pt-4 pb-3">
                <p className="text-sm font-medium text-gray-700 mb-2">只今ご案内できる最短の予約枠</p>
                <p className="text-lg font-bold text-gray-900">
                  {currentEarliestSchedule.formatted_date}　{currentEarliestSchedule.formatted_time}
                  <span className="text-sm font-normal text-gray-600 ml-2">担当: {currentEarliestSchedule.doctor.full_name} 先生</span>
                </p>
                <PrescriptionNotice
                  nightSlot={currentEarliestSchedule.night_slot}
                  expectedPrescriptionIssuedDate={currentEarliestSchedule.expected_prescription_issued_date}
                  pharmacyReceivableFrom={currentEarliestSchedule.pharmacy_receivable_from}
                  className="mt-1"
                />
              </div>
              <div className="px-4 pb-4">
                <button
                  type="button"
                  onClick={handleSelectEarliest}
                  disabled={isSubmittingEarliest}
                  className="button-primary"
                >
                  {isSubmittingEarliest ? '処理中...' : '一番近い日時で予約する'}
                </button>
              </div>
            </div>
          )}
          {!currentEarliestSchedule && selectedClinicId && !isLoadingSchedules && (
            <div className="mb-6 rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-5 text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">現在、予約可能な診療枠はありません</p>
                <p className="text-xs text-gray-500">時間をおいて再度ご確認いただくか、別の診療機関をお選びください。</p>
              </div>
            </div>
          )}

          {/* 週間カレンダー */}
          {selectedClinicId && (
            <div className="mb-6">
              <h2 className="text-center text-lg font-medium text-gray-800 mb-2">
                受診日時を選択してください
              </h2>
              <p className="text-center text-sm text-gray-500 mb-4">
                選択いただいた予約枠は30分間仮押さえできます
              </p>

              <WeeklyCalendar
                weeklySchedules={weeklySchedules}
                weekStartDate={currentWeekStart}
                selectedScheduleId={form.data.doctor_schedule_id || null}
                onSelectSchedule={handleSelectSchedule}
                onWeekChange={handleWeekChange}
                isLoading={isLoadingSchedules}
                stickyTop={bannerHeight}
              />
            </div>
          )}

          {/* 予約ボタン（診療科選択前から非活性で常時表示し、予約フロー内であることを認知させる） */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg">
            <div className="mx-auto max-w-3xl">
              {is_logged_in ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit || form.processing}
                  className="button-primary"
                >
                  {form.processing ? '処理中...' : '予約する'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleLoginRedirect}
                  disabled={!canSubmit}
                  className="button-primary"
                >
                  ログイン/新規登録をして予約
                </button>
              )}
            </div>
          </div>

          {/* ボタン分のスペース確保 */}
          <div className="h-24" />
        </main>
      </div>
    </>
  )
}
